import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../App';
import { LayoutDashboard, Package, Folder, Users, Activity, Settings, LogOut, Menu, X, Store } from 'lucide-react';
import { Button } from './ui/button';

const Layout = ({ children }) => {
  const { user, logout, selectedStore, selectStore, token } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stores, setStores] = useState([]);

  useEffect(() => {
    fetchStores();
  }, []);

  const fetchStores = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stores`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStores(data);
        
        // Auto-select first store if none selected and stores exist
        if (!selectedStore && data.length > 0) {
          selectStore(data[0].id);
        }
      }
    } catch (error) {
      console.error('Failed to load stores');
    }
  };

  const baseNavigation = [
    { name: 'Dashboard', path: '/', icon: LayoutDashboard },
    { name: 'Products', path: '/products', icon: Package },
    { name: 'Categories', path: '/categories', icon: Folder },
  ];

  const adminNavigation = user?.role === 'admin' ? [
    { name: 'Stores', path: '/stores', icon: Store },
    { name: 'Users', path: '/users', icon: Users },
    { name: 'Activity Logs', path: '/logs', icon: Activity }
  ] : [];

  const endNavigation = [
    { name: 'Settings', path: '/settings', icon: Settings },
  ];

  const navigation = [...baseNavigation, ...adminNavigation, ...endNavigation];

  const getCurrentStoreName = () => {
    const store = stores.find(s => s.id === selectedStore);
    return store ? store.name : 'All Stores';
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-card border-b border-border z-50 flex items-center justify-between px-4">
        <h1 className="font-heading font-bold text-xl">Vape Avenue</h1>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          data-testid="mobile-menu-toggle"
        >
          {sidebarOpen ? <X /> : <Menu />}
        </Button>
      </div>

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full w-64 bg-card border-r border-border transition-transform duration-200 z-40 flex flex-col ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0`}
      >
        <div className="p-6">
          <h1 className="font-heading font-extrabold text-2xl tracking-tight" data-testid="app-title">Vape Avenue</h1>
          <p className="text-xs text-muted-foreground font-mono mt-1">INVENTORY SYSTEM</p>
        </div>

        {/* Store Selector */}
        {stores.length > 0 && (
          <div className="px-3 mb-4">
            <label className="font-mono text-xs uppercase tracking-widest text-muted-foreground px-3 mb-2 block">
              Current Store
            </label>
            <select
              value={selectedStore || ''}
              onChange={(e) => selectStore(e.target.value || null)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              data-testid="store-selector"
            >
              <option value="">All Stores</option>
              {stores.map(store => (
                <option key={store.id} value={store.id}>{store.name}</option>
              ))}
            </select>
          </div>
        )}

        <nav className="px-3 space-y-1 flex-1 overflow-y-auto">
          {navigation.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-md transition-all ${
                  isActive
                    ? 'bg-primary text-primary-foreground font-medium'
                    : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                }`}
              >
                <Icon size={20} strokeWidth={1.5} />
                <span className="font-body">{item.name}</span>
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-border">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center font-heading font-bold text-sm">
              {user?.name?.charAt(0)?.toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-body font-medium text-sm truncate">{user?.name}</p>
              <p className="font-mono text-xs text-muted-foreground uppercase">{user?.role}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start text-muted-foreground hover:text-foreground"
            onClick={logout}
            data-testid="logout-btn"
          >
            <LogOut size={18} strokeWidth={1.5} className="mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0">
        <div className="p-6 md:p-8 lg:p-12">
          {children}
        </div>
      </main>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default Layout;
